// --- Our Modules --------------------------------------------------------------------------------
import type useTranslation from "@/hooks/useTranslation";
import type { TyStatsRange } from "@/types";
import { formatMoney as sharedMoney } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type Ty = ReturnType<typeof useTranslation>;

export type TyBucketUnit = "hour" | "day" | "month";

// --- Constants ----------------------------------------------------------------------------------

/**
 * The backend buckets every series in Cairo time, so the axis has to be built in Cairo time too —
 * grouping client-side in the browser's zone would slide every label by the offset between them.
 */
const TIMEZONE = "Africa/Cairo";

const HOUR_MS = 3_600_000;

/** A runaway range must not try to draw ten thousand columns. */
const MAX_BUCKETS = 400;

// --- Keys ---------------------------------------------------------------------------------------

/**
 * The Cairo calendar slot a timestamp falls in, as a sortable string. Compared as text rather than as
 * dates on purpose: a bucket boundary the server truncated in Cairo is not a round number in UTC, and
 * Egypt observes DST — so `Date` equality between "the server's bucket" and "a bucket we computed"
 * is wrong twice a year, while the formatted calendar slot is right always.
 */
export function bucketKey(value: string | Date, unit: TyBucketUnit): string {
    const date = new Date(value);
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        hour12: false,
    }).formatToParts(date);

    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
    const day = `${get("year")}-${get("month")}-${get("day")}`;

    if (unit === "month") return day.slice(0, 7);
    if (unit === "day") return day;
    return `${day} ${get("hour")}`;
}

/**
 * Every slot between two instants, so a series with three scattered sales draws three spikes on a real
 * calendar instead of three neighbours. Steps are anchored at midday, where a one-hour DST shift can
 * never carry the cursor across a Cairo midnight and drop a day from the axis.
 */
export function buildBuckets(from: string | null, to: string, unit: TyBucketUnit, fallbackFrom?: string): Date[] {
    const startRaw = from ?? fallbackFrom;
    if (!startRaw) return [];

    const start = new Date(startRaw);
    const end = new Date(to);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];

    const buckets: Date[] = [];
    const endKey = bucketKey(end, unit);

    if (unit === "hour") {
        const cursor = new Date(Math.floor(start.getTime() / HOUR_MS) * HOUR_MS);
        while (cursor <= end && buckets.length < MAX_BUCKETS) {
            buckets.push(new Date(cursor));
            cursor.setTime(cursor.getTime() + HOUR_MS);
        }
        return buckets;
    }

    // Midday UTC is early afternoon in Cairo — far enough from both midnights to survive a DST hour.
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), unit === "month" ? 15 : start.getUTCDate(), 12));

    while (buckets.length < MAX_BUCKETS) {
        buckets.push(new Date(cursor));
        if (bucketKey(cursor, unit) === endKey) break;
        if (cursor > end) break;
        if (unit === "month") cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        else cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    return buckets;
}

// --- Formatting ---------------------------------------------------------------------------------

/**
 * Re-exported rather than redefined: the tables needed the same number, money and date formatting, and
 * two copies would drift the moment one screen changed how a currency reads.
 */
export { formatDate, formatDateTime, formatMs, formatNumber, formatPercent } from "@/utils/format";

/** Axis labels: short enough to sit under a column without rotating. */
export function formatBucket(value: string | Date, unit: TyBucketUnit, lang: string | null): string {
    const date = new Date(value);
    const options: Intl.DateTimeFormatOptions =
        unit === "hour"
            ? { timeZone: TIMEZONE, hour: "2-digit", hour12: false }
            : unit === "month"
              ? { timeZone: TIMEZONE, month: "short", year: "2-digit" }
              : { timeZone: TIMEZONE, day: "numeric", month: "short" };

    return new Intl.DateTimeFormat(lang === "en" ? "en-EG" : "ar-EG", options).format(date);
}

/** Money needs the translated unit, so it stays a thin wrapper over the shared formatter. */
export function formatMoney(value: number, lang: string | null, t: Ty): string {
    return sharedMoney(value, lang, t.home.egp);
}

// --- Labels -------------------------------------------------------------------------------------

/**
 * The name a greeting should use. "Good evening, Ahmed M. Zanaty" reads like a bank letter — the whole
 * point of greeting somebody by name is that it sounds like a person, so only the first word survives.
 * Works the same for Arabic names, which are given-name-first too.
 */
export function firstName(name: string | null | undefined): string {
    return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

export function rangeLabel(range: TyStatsRange, t: Ty): string {
    const labels: Record<TyStatsRange, string> = {
        today: t.home.rangeToday,
        "7d": t.home.range7d,
        "30d": t.home.range30d,
        "90d": t.home.range90d,
        "12m": t.home.range12m,
        all: t.home.rangeAll,
        custom: t.home.rangeCustom,
    };
    return labels[range] ?? t.home.rangeCustom;
}

/**
 * Lines one series up against a prepared axis. Anything the server did not send for a slot is a real
 * zero — the row simply had no sales that day — so filling it keeps the shape of time honest.
 */
export function alignToBuckets<T extends { at: string }>(rows: T[], buckets: Date[], unit: TyBucketUnit, pick: (row: T) => number): number[] {
    const byKey = new Map<string, number>();
    for (const row of rows) {
        const key = bucketKey(row.at, unit);
        byKey.set(key, (byKey.get(key) ?? 0) + pick(row));
    }
    return buckets.map((slot) => byKey.get(bucketKey(slot, unit)) ?? 0);
}
